/**
 * Regel-Kontext des Umsatzsteuer-Rechenkerns: sortiert die Eingaben deterministisch (R12)
 * und baut die Indizes, die alle Regel-Dateien brauchen (Periods, Einheiten, Buchungen,
 * Zahlungen je Buchung).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {BookingLite, ExpenseLite, PaymentLite, PeriodLite, RuleContext, UnitLite, VatEngineInput} from './types';

export const compareByID = <T extends { id: string }>(a: T, b: T): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const sortByID = <T extends { id: string }>(items: T[]): T[] => [...items].sort(compareByID);

const time = (d: Date | null): number => (d ? d.getTime() : Number.NEGATIVE_INFINITY);

export const comparePaymentsByDateThenID = (a: PaymentLite, b: PaymentLite): number => {
    const ta = time(a.date);
    const tb = time(b.date);
    return ta !== tb ? (ta < tb ? -1 : 1) : compareByID(a, b);
};

const compareStrings = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Vergleich zweier Zeitpunkte, bei denen null als „seit jeher“/„laufend“ zählt (−∞ − −∞ wäre NaN). */
const compareTimes = (a: Date | null, b: Date | null): number => {
    const ta = time(a);
    const tb = time(b);
    return ta === tb ? 0 : ta < tb ? -1 : 1;
};

/** Kopie der Eingabe mit deterministisch sortierten Arrays; die Original-Arrays bleiben unangetastet. */
export const sortEngineInput = (input: VatEngineInput): VatEngineInput => ({
    ...input,
    ownership: [...input.ownership].sort((a, b) =>
        compareStrings(a.propertyID, b.propertyID)
        || compareStrings(a.landlordID, b.landlordID)
        || compareTimes(a.from, b.from)
        || compareTimes(a.to, b.to)),
    pendingChanges: [...input.pendingChanges].sort((a, b) =>
        compareStrings(a.propertyID, b.propertyID) || compareTimes(a.performAt, b.performAt)),
    periods: sortByID(input.periods),
    units: sortByID(input.units),
    bookings: sortByID(input.bookings),
    payments: sortByID(input.payments),
    priorPayments: sortByID(input.priorPayments),
    expenses: sortByID(input.expenses),
    adjustments: sortByID(input.adjustments),
});

const groupBy = <T>(items: T[], key: (item: T) => string | null): Map<string, T[]> => {
    const map = new Map<string, T[]>();
    for (const item of items) {
        const k = key(item);
        if (k === null) {
            continue;
        }
        const list = map.get(k) ?? [];
        list.push(item);
        map.set(k, list);
    }
    return map;
};

export const buildRuleContext = (rawInput: VatEngineInput): RuleContext => {
    const input = sortEngineInput(rawInput);

    const periodsByID = new Map<string, PeriodLite>(input.periods.map(p => [p.id, p]));
    const periodsByUnit = groupBy(input.periods, p => p.unitID);
    for (const list of periodsByUnit.values()) {
        list.sort((a, b) => compareTimes(a.startDate, b.startDate) || compareByID(a, b));
    }

    const unitsByProperty = groupBy(input.units, u => u.propertyID);
    const unitsByID = new Map<string, UnitLite>(input.units.map(u => [u.id, u]));
    const bookingsByID = new Map<string, BookingLite>(input.bookings.map(b => [b.id, b]));

    // payments ∪ priorPayments: gleiche IDs (Zahlung liegt in beiden Fenstern) zählen nur einmal
    const paymentPool = new Map<string, PaymentLite>();
    for (const p of [...input.payments, ...input.priorPayments]) {
        paymentPool.set(p.id, p);
    }
    const paymentsByBooking = groupBy(Array.from(paymentPool.values()), p => p.bookingID);
    for (const list of paymentsByBooking.values()) {
        list.sort(comparePaymentsByDateThenID);
    }
    const expensesByID = new Map<string, ExpenseLite>(input.expenses.map(e => [e.id, e]));

    return {
        landlordID: input.landlordID,
        range: input.range,
        now: input.now,
        profileAt: input.profileAt,
        periodsByID,
        periodsByUnit,
        unitsByProperty,
        unitsByID,
        bookingsByID,
        paymentsByID: paymentPool,
        paymentsByBooking,
        expensesByID,
        propertyProfiles: input.propertyProfiles,
        adjustments: input.adjustments,
        ownership: input.ownership,
        pendingChanges: input.pendingChanges,
    };
};
